// import cl from 'classnames'
import {
  formatPrice, ImageSquare, PaginationButtons, PaginationButtonsProps, SortDirection, TableBodyContainer,
  TableBodyLine,
  TableBodyLineProps, TableContainer,
  TableHeader,
  TableHeaderProps, Text1
} from '@touche-lts/panel-ui/dist'
import { observer } from 'mobx-react-lite'
import { useRootStore } from 'mobx-stores/store-provider'
import { StatisticsTableSortProps } from 'mobx-stores/utils'
import { StatisticsStores } from 'models/statistics'
import { useEffect, useState } from 'react'
import _ from 'lodash';
import TableBody from './table-body'
import styles from './statistics-table.module.scss'

function getRowStoreSortKey(row: any): string {
  const s = row?.storeName ?? row?.store?.name ?? row?.name ?? ''
  return String(s).toLowerCase()
}

const StatisticsTableComponent = () => {
  const {
    statisticsTableStore: {
      isColumnMarked,
      getSortDirection,
      handleSortDirectionChange,
      searchProps,
      onSearchPropsChange,
      setPaginationProps,
      searchResults,
      // paginationInfo, // actiate if bd handles pagination in future
      // onPageSelect, // actiate if bd handles pagination in future
      storeSelected
    }
  } = useRootStore()

  useEffect(() => {
    if (searchResults) {
      searchResults.forEach(store => {
        _.merge(store, store?.store)
      })
    }
  }, [searchResults]);

  const [pageSelected, setPageSelected] = useState(0); // define current page selected
  const [elementsPerPage, setElementsPerPage] = useState(10); // define elements per page
  const [productsVisible, setProductsVisible] = useState(true);

  const rows = searchResults ?? []

  const searchResultsPaginated = () => {
    const startIndexPage = pageSelected * elementsPerPage;
    return rows.slice(startIndexPage, startIndexPage + elementsPerPage)
  }

  const sortColumnTable = (sortVaue: StatisticsTableSortProps): void => {
    let sortDirection = getSortDirection(sortVaue);
    if (sortDirection === SortDirection.ASC) {
      sortDirection = SortDirection.DESC
    } else {
      sortDirection = SortDirection.ASC
    }
    setPaginationProps({
      sortProp: sortVaue,
      sortDirection
    } as any)
    if (!rows.length) return
    if (sortDirection === SortDirection.ASC) {
      rows.sort((a, b) => Number(a[sortVaue] ?? 0) - Number(b[sortVaue] ?? 0));
    } else {
      rows.sort((a, b) => Number(b[sortVaue] ?? 0) - Number(a[sortVaue] ?? 0));
    }
  }

  const sortByName = (sortVaue: StatisticsTableSortProps): void => {
    let sortDirection = getSortDirection(sortVaue);
    if (sortDirection === SortDirection.ASC) {
      sortDirection = SortDirection.DESC
    } else {
      sortDirection = SortDirection.ASC
    }
    setPaginationProps({
      sortProp: sortVaue,
      sortDirection
    } as any)
    if (!rows.length) return
    if (sortDirection === SortDirection.ASC) {
      rows.sort((a, b) => getRowStoreSortKey(a).localeCompare(getRowStoreSortKey(b)));
    } else {
      rows.sort((a, b) => getRowStoreSortKey(b).localeCompare(getRowStoreSortKey(a)));
    }
  }

  const tableColumns: TableHeaderProps['tableColumns'] = [
    (
      storeSelected === StatisticsStores.ALLSTORES ?
        (
          {
            label: 'product',
            isColumnMarked: isColumnMarked(StatisticsTableSortProps.PRODUCT_COUNT),
            sortDirection: getSortDirection(StatisticsTableSortProps.PRODUCT_COUNT),
            // setSortDirection: handleSortDirectionChange(
            //   StatisticsTableSortProps.PRODUCT_COUNT
            // ) // actiate if bd handles pagination in future
            setSortDirection: () => sortColumnTable(StatisticsTableSortProps.PRODUCT_COUNT),
          }
        )
        :
        {
          label: 'product',
          isColumnMarked: isColumnMarked(StatisticsTableSortProps.PRODUCT_NAME),
          sortDirection: getSortDirection(StatisticsTableSortProps.PRODUCT_NAME),
          // setSortDirection: handleSortDirectionChange(
          //   StatisticsTableSortProps.PRODUCT_NAME
          // ), // actiate if bd handles pagination in future
          setSortDirection: () => sortColumnTable(StatisticsTableSortProps.PRODUCT_NAME),
          inputProps: {
            placeholder: 'Search...',
            value: searchProps.productName,
            onChange: productName => onSearchPropsChange({ productName })
          }
        }
    ),
    {
      label: 'active skus',
      isColumnMarked: isColumnMarked(StatisticsTableSortProps.ACTIVE_SKUS),
      sortDirection: getSortDirection(StatisticsTableSortProps.ACTIVE_SKUS),
      // setSortDirection: handleSortDirectionChange(
      //   StatisticsTableSortProps.ACTIVE_SKUS
      // ), // actiate if bd handles pagination in future
      setSortDirection: () => sortColumnTable(StatisticsTableSortProps.ACTIVE_SKUS),
      sliderFilterProps: {
        label: 'active skus',
        min: 0,
        max: 1000,
        value: [searchProps.activeSkusMin, searchProps.activeSkusMax],
        onChange: ([activeSkusMin, activeSkusMax]) =>
          onSearchPropsChange({ activeSkusMin, activeSkusMax })
      }
    },
    {
      label: 'inventory',
      isColumnMarked: isColumnMarked(StatisticsTableSortProps.INVENTORY),
      sortDirection: getSortDirection(StatisticsTableSortProps.INVENTORY),
      // setSortDirection: handleSortDirectionChange(
      //   StatisticsTableSortProps.INVENTORY
      // ), // actiate if bd handles pagination in future
      setSortDirection: () => sortColumnTable(
        StatisticsTableSortProps.INVENTORY
      ),
      sliderFilterProps: {
        label: 'items in stock',
        min: 0,
        max: 1000,
        value: [searchProps.stockMin, searchProps.stockMax],
        onChange: ([stockMin, stockMax]) =>
          onSearchPropsChange({ stockMin, stockMax })
      }
    },
    {
      label: 'sold',
      isColumnMarked: isColumnMarked(StatisticsTableSortProps.SOLD),
      sortDirection: getSortDirection(StatisticsTableSortProps.SOLD),
      // setSortDirection: handleSortDirectionChange(StatisticsTableSortProps.SOLD), // actiate if bd handles pagination in future
      setSortDirection: () => sortColumnTable(StatisticsTableSortProps.SOLD),
      sliderFilterProps: {
        label: 'items sold',
        min: 0,
        max: 1000,
        value: [searchProps.soldMin, searchProps.soldMax],
        onChange: ([soldMin, soldMax]) =>
          onSearchPropsChange({ soldMin, soldMax })
      }
    },
    {
      label: 'revenue',
      isColumnMarked: isColumnMarked(StatisticsTableSortProps.REVENUE),
      sortDirection: getSortDirection(StatisticsTableSortProps.REVENUE),
      // setSortDirection: handleSortDirectionChange(
      //   StatisticsTableSortProps.REVENUE
      // ), // actiate if bd handles pagination in future
      setSortDirection: () => sortColumnTable(StatisticsTableSortProps.REVENUE),
      sliderFilterProps: {
        label: 'usd',
        min: 0,
        max: 1000,
        value: [searchProps.minRevenue, searchProps.maxRevenue],
        onChange: ([minRevenue, maxRevenue]) =>
          onSearchPropsChange({ minRevenue, maxRevenue })
      }
    }
  ]

  if (storeSelected === StatisticsStores.ALLSTORES) {
    tableColumns.unshift(
      {
        label: 'store',
        isColumnMarked: isColumnMarked(StatisticsTableSortProps.STORE_NAME),
        sortDirection: getSortDirection(StatisticsTableSortProps.STORE_NAME),
        // setSortDirection: handleSortDirectionChange(
        //   StatisticsTableSortProps.STORE_NAME
        // ), // actiate if bd handles pagination in future
        setSortDirection: () => sortByName(StatisticsTableSortProps.STORE_NAME),
        inputProps: {
          placeholder: 'Search...',
          value: searchProps.storeName,
          onChange: storeName => onSearchPropsChange({ storeName })
        }
      }
    )
  }

  const getBodyLineCells = (
    store: any
  ): TableBodyLineProps['cells'] => {
    const cells: TableBodyLineProps['cells'] = [
      {
        children: (
          <>
            {storeSelected !== StatisticsStores.ALLSTORES && (
              <ImageSquare src={store?.images && store?.images[0]?.url} width={30} />
            )}
            <Text1
              bold
              overflowEllipsis
              style={{ marginLeft: 8 }}
            >
              {storeSelected === StatisticsStores.ALLSTORES ? (store?.productCount + ' products') : store?.productName}
            </Text1>
          </>
        ),
        isColumnMarked: isColumnMarked(StatisticsTableSortProps.PRODUCT_NAME)
      },
      {
        children: store?.activeSkus, // TODO-A store.activeSkuCount
        isColumnMarked: isColumnMarked(StatisticsTableSortProps.ACTIVE_SKUS)
      },
      {
        children: (
          <div>
            <Text1 overflowEllipsis>
              {store?.stock || 0} in stock
            </Text1>
            <Text1 overflowEllipsis>
              ({store.variants || 0} variant
              {store.variants === 1 ? '' : 's'})
            </Text1>
          </div>
        ),
        isColumnMarked: isColumnMarked(StatisticsTableSortProps.INVENTORY)
      },
      {
        children: store?.sold || 0,
        isColumnMarked: isColumnMarked(StatisticsTableSortProps.SOLD)
      },
      {
        children: formatPrice(store?.revenueTotal),
        isColumnMarked: isColumnMarked(StatisticsTableSortProps.REVENUE)
      }
    ]

    if (storeSelected === StatisticsStores.ALLSTORES) {
      cells.unshift(
        {
          children: (
            <>
              {
                store?.store?.images && store?.store?.images[0]?.url && store?.store?.images[0]?.url.includes('.mp4')
                  ?
                  <video width={30}>
                    <source src={store?.store?.images[0]?.url} type="video/mp4" />
                  </video>
                  :
                  <ImageSquare src={store?.store?.images[0]?.url} width={30} />
              }
              <Text1
                bold
                overflowEllipsis
                style={{ marginLeft: 8 }}
              >
                {store?.store?.name}
              </Text1>
            </>
          ),
          isColumnMarked: isColumnMarked(StatisticsTableSortProps.PRODUCT_NAME)
        }
      )
    }

    return cells
  }

  return (
    <TableContainer
      {...{
        noBoxShadow: true,
        style: { marginTop: 15, minHeight: 230, overflowX: 'auto'},
      }}
      className={styles.thinScroll}
    >
      <TableHeader tableColumns={tableColumns} />
      <TableBodyContainer className={styles.tableBodyContainer}>
        <TableBody
          {...{
            elements: searchResultsPaginated,
            productsVisible,
            getBodyLineCells,
          }}
        />
      </TableBodyContainer>
      {/**Pagination deactivated for now */}
      {!!rows.length && (
        <PaginationButtons
          alwaysShow
          totalPages={Math.ceil(rows.length / elementsPerPage)}
          selectedPageNumber={pageSelected}
          totalElements={rows.length}
          elementsPerPage={elementsPerPage}
          // {...handlePagination()} // actiate if bd handles pagination in future
          onPageSelect={(pageNumber) => {
            const el = document.getElementById('statistics-top-row')
            if (el) el.scrollIntoView()
            setPageSelected(pageNumber);
            setProductsVisible(false);
            setTimeout(() => {
              setProductsVisible(true);
            }, 50);
            // onPageSelect(pageNumber) // actiate if bd handles pagination in future
          }}
        />
      )}
    </TableContainer>
  );
}

const StatisticsTable = observer(StatisticsTableComponent)
export { StatisticsTable }
